import React, { forwardRef, useEffect, useRef, useImperativeHandle, useState, useCallback } from 'react';

import { useAuth } from '../../context/AuthContext';
import { errorMessage } from '../../services/apiError';
import { eventService } from '../../services/eventService';
import { projectService } from '../../services/projectService';
import type { ProjectCreditBillingSource, ProjectCreditSourceStatus } from '../../services/projectService';
import { useAppSelector } from '../../store/hooks';
import { BrandPendulumMark } from '../BrandPendulumMark';
import { ModelSelectDropdown } from '../ModelSelectDropdown';
import { useWorkspaceLoadingSignal } from '../PlanningWorkspaceGate/WorkspaceReadinessContext';
import { ProjectVisibilityDropdown } from '../ProjectVisibilityDropdown';
import { useToast } from '../Toast';

import { AgentsModal } from './AgentsModal';
import { MediaPreview } from './MediaPreview';
import { useCompactComposer } from './useCompactComposer';
import { useDocProcessing } from './useDocProcessing';
import { useMediaDrop } from './useMediaDrop';
import { useMediaUpload } from './useMediaUpload';


// ============================================================================
// ICONS
// ============================================================================

const SendIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="send-icon"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const StopIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className="send-icon"
    aria-hidden="true"
  >
    <rect x="7" y="7" width="10" height="10" rx="2" />
  </svg>
);

const AttachIcon: React.FC = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);



// ============================================================================
// MODEL DROPDOWN
// ============================================================================

export type ModelTier = 'fast' | 'spark' | 'standard' | 'large' | 'super_large' | 'byok_standard' | 'byok_large' | 'byok_super_large';

interface ModelOption {
  value: ModelTier;
  label: string;
  multiplier: string;
}

const MODEL_OPTIONS: ModelOption[] = [
  // commented as tasks are failing for these models.
  // { value: 'fast', label: 'Small AI', multiplier: '0.5x' },
  // { value: 'spark', label: 'Spark AI', multiplier: '1x' },
  // { value: 'standard', label: 'Standard AI', multiplier: '2x' },
  { value: 'large', label: 'Large AI', multiplier: '6x' },
  { value: 'super_large', label: 'Super Large AI', multiplier: '10x' },
];

const BYOK_OPTIONS: ModelOption[] = [
  { value: 'byok_standard', label: 'BYOK Standard', multiplier: 'Your Key' },
  { value: 'byok_large', label: 'BYOK Large', multiplier: 'Your Key' },
  { value: 'byok_super_large', label: 'BYOK Super Large', multiplier: 'Your Key' },
];

export const MODEL_MULTIPLIERS: Record<ModelTier, number> = {
  fast: 0.5,
  spark: 1,
  standard: 2,
  large: 6,
  super_large: 10,
  byok_standard: 1,
  byok_large: 1,
  byok_super_large: 1,
};

const formatComposerCredits = (value?: number | null): string => {
  if (value === null || value === undefined) return '0';
  if (Number.isInteger(value)) return value.toFixed(0);
  if (Math.abs(value) >= 100) return value.toFixed(0);
  if (Math.abs(value) >= 10) return value.toFixed(1);

  return value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
};

interface ModelDropdownProps {
  value: ModelTier;
  onChange: (value: ModelTier) => void;
  hasByokKeys?: boolean;
  isByokPlan?: boolean;
  menuPlacement?: 'up' | 'down' | 'auto';
  disabled?: boolean;
  options?: ModelTier[];
}

const buildOptions = (hasByokKeys?: boolean, isByokPlan?: boolean): ModelOption[] => (
  isByokPlan
    ? BYOK_OPTIONS
    : [...MODEL_OPTIONS, ...(hasByokKeys ? BYOK_OPTIONS : [])]
);

const isClippingOverflow = (value: string): boolean => (
  value === 'auto' || value === 'scroll' || value === 'hidden' || value === 'clip'
);

const getVisibleVerticalBounds = (element: HTMLElement): { top: number; bottom: number } => {
  let top = 12;
  let bottom = window.innerHeight - 12;
  let current: HTMLElement | null = element.parentElement;

  while (current) {
    const styles = window.getComputedStyle(current);
    const clipsVertically = isClippingOverflow(styles.overflowY) || isClippingOverflow(styles.overflow);

    if (clipsVertically) {
      const rect = current.getBoundingClientRect();
      top = Math.max(top, rect.top);
      bottom = Math.min(bottom, rect.bottom);
    }

    current = current.parentElement;
  }

  return { top, bottom };
};

const useClickOutside = (ref: React.RefObject<HTMLDivElement | null>, onClose: () => void, active: boolean) => {
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };

    document.addEventListener('mousedown', handler);

    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose, active]);
};

const ModelDropdown: React.FC<ModelDropdownProps> = ({
  value,
  onChange,
  hasByokKeys,
  isByokPlan,
  menuPlacement = 'up',
  disabled = false,
  options,
}) => {
  const [open, setOpen] = useState(false);
  const [resolvedMenuPlacement, setResolvedMenuPlacement] = useState<'up' | 'down'>(
    menuPlacement === 'down' ? 'down' : 'up'
  );
  const [menuMaxHeight, setMenuMaxHeight] = useState<number | undefined>(undefined);
  const wrapRef = useRef<HTMLDivElement>(null);
  const availableOptions = buildOptions(hasByokKeys, isByokPlan);
  const allOptions = options
    ? availableOptions.filter((option) => options.includes(option.value))
    : availableOptions;
  const selected = allOptions.find((o) => o.value === value);

  useClickOutside(wrapRef, () => setOpen(false), open);

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  const updatePlacement = useCallback(() => {
    if (!wrapRef.current) return;

    if (menuPlacement !== 'auto') {
      setResolvedMenuPlacement(menuPlacement === 'down' ? 'down' : 'up');
      setMenuMaxHeight(undefined);
      return;
    }

    const rect = wrapRef.current.getBoundingClientRect();
    const { top: visibleTop, bottom: visibleBottom } = getVisibleVerticalBounds(wrapRef.current);
    const estimatedMenuHeight = (allOptions.length * 40) + 16;
    const spaceAbove = Math.max(0, rect.top - visibleTop);
    const spaceBelow = Math.max(0, visibleBottom - rect.bottom);
    const shouldOpenDown = spaceBelow >= estimatedMenuHeight
      || (spaceBelow >= 120 && spaceBelow >= spaceAbove);
    const availableSpace = shouldOpenDown ? spaceBelow : spaceAbove;

    setResolvedMenuPlacement(shouldOpenDown ? 'down' : 'up');
    setMenuMaxHeight(availableSpace > 0 ? Math.max(120, Math.min(availableSpace, 320)) : undefined);
  }, [allOptions.length, menuPlacement]);

  useEffect(() => {
    if (!open) return undefined;

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);

    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [open, updatePlacement]);

  return (
    <div className="model-dropdown-wrapper" ref={wrapRef}>
      <button
        type="button" className="model-dropdown"
        onClick={() => {
          if (!disabled) {
            if (!open) {
              updatePlacement();
            }
            setOpen((p) => !p);
          }
        }}
        aria-haspopup="listbox" aria-expanded={open}
        disabled={disabled}
      >
        <span className="model-dropdown__label">
          {selected ? (
            <>
              <span className="model-dropdown__name">{selected.label}</span>
              <span className="model-dropdown__multiplier"> ({selected.multiplier})</span>
              <span className="model-dropdown__mobile-multiplier">{selected.multiplier}</span>
            </>
          ) : 'Select'}
        </span>
        <svg className="model-dropdown__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <ul
          className={`model-dropdown__menu${resolvedMenuPlacement === 'down' ? ' model-dropdown__menu--down' : ''}`}
          role="listbox"
          style={menuMaxHeight ? { maxHeight: `${menuMaxHeight}px`, overflowY: 'auto' } : undefined}
        >
          {allOptions.map((opt) => (
            <li
              key={opt.value} role="option" aria-selected={opt.value === value}
              className={`model-dropdown__item${opt.value === value ? ' model-dropdown__item--active' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.value === value && <span className="model-dropdown__check">✓</span>}
              {opt.label} ({opt.multiplier})
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export { ModelDropdown };

// ============================================================================
// HELPERS
// ============================================================================

interface InputHandlers {
  trimmedInput: string;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handleSendClick: () => void;
}

const createInputHandlers = (
  value: string,
  isLoading: boolean,
  handler?: (input: string) => void
): InputHandlers => {
  const trimmedInput = value.trim();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && trimmedInput && !isLoading) {
      e.preventDefault();
      handler?.(value);
    }
  };

  const handleSendClick = () => {
    if (trimmedInput && !isLoading) handler?.(value);
  };

  return { trimmedInput, handleKeyDown, handleSendClick };
};

// ============================================================================
// TEXT INPUT
// ============================================================================

interface TextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
}

const TextInput = forwardRef<HTMLTextAreaElement, TextInputProps>(
  ({ value, onChange, placeholder, disabled = false, maxLength }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => internalRef.current as HTMLTextAreaElement);

    useEffect(() => {
      if (internalRef.current) {
        internalRef.current.style.height = 'auto';
        internalRef.current.style.height = `${Math.min(internalRef.current.scrollHeight, 200)}px`;
      }
    }, [value]);

    // Auto-focus the textarea the moment the AI finishes streaming a reply,
    // so the user can immediately type a follow-up without clicking.
    useEffect(() => {
      const handler = () => internalRef.current?.focus();

      window.addEventListener('chat_stream_end', handler);

      return () => window.removeEventListener('chat_stream_end', handler);
    }, []);

    return (
      <textarea
        ref={internalRef}
        className="text-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        rows={1}
      />
    );
  }
);

TextInput.displayName = 'TextInput';

// ============================================================================
// BUTTONS
// ============================================================================

interface SendButtonProps {
  isLoading: boolean;
  disabled: boolean;
  onClick: () => void;
  className?: string;
  isStop?: boolean;
  isStopping?: boolean;
}

const SendButton: React.FC<SendButtonProps> = ({ isLoading, disabled, onClick, className, isStop, isStopping }) => (
  <button
    className={[
      'action-btn send-btn app-action-btn',
      isStop ? 'send-btn--stop app-action-btn--danger' : 'app-action-btn--primary',
      className,
    ].filter(Boolean).join(' ')}
    onClick={onClick}
    disabled={disabled}
    type="button"
    aria-busy={isLoading || isStopping}
    aria-label={isStopping ? 'Stopping' : isStop ? 'Stop' : 'Send'}
    title={isStopping ? 'Stopping' : isStop ? 'Stop' : 'Send'}
  >
    {isStopping ? <BrandPendulumMark animated={false} className="send-pendulum" /> : isStop ? <StopIcon /> : isLoading ? <BrandPendulumMark animated={false} className="send-pendulum" /> : <SendIcon />}
    <span className="send-text">{isStopping ? 'Stopping' : isStop ? 'Stop' : 'Send'}</span>
  </button>
);

interface ActionButtonProps {
  onClick?: () => void;
  disabled?: boolean;
}

const AttachButton: React.FC<ActionButtonProps> = ({ onClick, disabled = false }) => (
  <button
    className="action-btn"
    onClick={onClick}
    disabled={disabled}
    type="button"
    title={disabled ? 'Attachments unavailable in this mode' : 'Attach files'}
  >
    <AttachIcon />
    <span className="action-btn-label">Attach</span>
  </button>
);

const ProjectCreditSourceControl: React.FC<{
  projectId: string | null;
  disabled?: boolean;
  isReadOnly?: boolean;
}> = ({ projectId, disabled = false, isReadOnly = false }) => {
  const toast = useToast();
  const [status, setStatus] = useState<ProjectCreditSourceStatus | null>(null);
  const [saving, setSaving] = useState(false);
  // Project whose credit-source status has been fetched at least once; the
  // control only appears after that, so the workspace gate waits for it.
  const [settledProjectId, setSettledProjectId] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const requestedProjectRef = useRef<string | null>(null);

  useWorkspaceLoadingSignal(
    'composer-credit-source',
    Boolean(projectId) && !isReadOnly && settledProjectId !== projectId,
  );

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadStatus = useCallback(async () => {
    if (!projectId || isReadOnly) return setStatus(null);

    requestedProjectRef.current = projectId;
    const nextStatus = await projectService.getProjectCreditSource(projectId).catch(() => null);

    // A slow response for a previous project must not overwrite the current one.
    if (!mountedRef.current || requestedProjectRef.current !== projectId) return;
    setStatus(nextStatus);
    setSettledProjectId(projectId);
  }, [isReadOnly, projectId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!projectId || isReadOnly) return undefined;

    let refreshTimer: number | null = null;
    const refresh = () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        void loadStatus();
      }, 160);
    };

    const onCreditsUpdate = (data: unknown) => {
      const payload = data as { project_id?: string | null; triggered_by?: string | null };

      if (payload.project_id && payload.project_id !== projectId) return;
      if (payload.triggered_by && status?.user_id && payload.triggered_by !== status.user_id) return;
      refresh();
    };

    const onWindowCreditsUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ project_id?: string | null; triggered_by?: string | null }>).detail;

      if (detail?.project_id && detail.project_id !== projectId) return;
      if (detail?.triggered_by && status?.user_id && detail.triggered_by !== status.user_id) return;
      refresh();
    };

    const unsubscribeCredits = eventService.subscribe('credits_update', onCreditsUpdate);
    const refreshAfterActivity = () => refresh();

    window.addEventListener('credits_update', onWindowCreditsUpdate);
    window.addEventListener('chat_stream_end', refreshAfterActivity);
    window.addEventListener('chat_sessions_changed', refreshAfterActivity);
    window.addEventListener('focus', refreshAfterActivity);

    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      unsubscribeCredits();
      window.removeEventListener('credits_update', onWindowCreditsUpdate);
      window.removeEventListener('chat_stream_end', refreshAfterActivity);
      window.removeEventListener('chat_sessions_changed', refreshAfterActivity);
      window.removeEventListener('focus', refreshAfterActivity);
    };
  }, [isReadOnly, loadStatus, projectId, status?.user_id]);

  if (!projectId || isReadOnly || !status?.is_org_project || !status.can_use_org_credit) return null;

  if (status.is_unlimited) {
    return (
      <div className="composer-credit-source" title="Unlimited project usage">
        <i className="fa-solid fa-infinity composer-credit-source__icon" aria-hidden="true" />
        <span className="composer-credit-source__balance">
          Unlimited {status.unlimited_scope === 'organization' ? 'org' : 'personal'} credits
        </span>
      </div>
    );
  }

  const selectedBalance = status.credit_billing_source === 'org_credit'
    ? status.org_credits_balance
    : status.own_credits_balance;

  const updateSource = async (source: ProjectCreditBillingSource) => {
    setSaving(true);
    try {
      const nextStatus = await projectService.updateProjectCreditSource(projectId, source);
      setStatus(nextStatus);
      toast.success(`Using ${source === 'org_credit' ? 'Org credits' : 'Own credits'}.`);
    } catch (err) {
      toast.error(errorMessage(err, 'We couldn’t update the credit source. Check the selection and try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="composer-credit-source" title="Project credit source">
      <i className="fa-solid fa-wallet composer-credit-source__icon" aria-hidden="true" />
      <span className="composer-credit-source__select-wrap">
        <select
          className="composer-credit-source__select"
          aria-label="Project credit source"
          value={status.credit_billing_source}
          disabled={disabled || saving}
          onChange={(event) => updateSource(event.target.value as ProjectCreditBillingSource)}
        >
          <option value="org_credit">Org credits</option>
          <option value="own_credit">Own credits</option>
        </select>
        <i className="fa-solid fa-chevron-down composer-credit-source__chevron" aria-hidden="true" />
      </span>
      <span className="composer-credit-source__balance">
        {formatComposerCredits(selectedBalance)} left
      </span>
    </div>
  );
};




// ============================================================================
// INPUT ROW & ACTIONS ROW
// ============================================================================

interface InputRowProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onClick: () => void;
  isLoading: boolean;
  disabled: boolean;
  inputDisabled?: boolean;
  inputRef: React.Ref<HTMLTextAreaElement>;
  placeholder?: string;
  showSendButton: boolean;
  isStop?: boolean;
  isStopping?: boolean;
  maxLength?: number;
}

const InputRow: React.FC<InputRowProps> = ({
  value,
  onChange,
  onKeyDown,
  onClick,
  isLoading,
  disabled,
  inputDisabled = false,
  inputRef,
  placeholder,
  showSendButton,
  isStop,
  isStopping,
  maxLength,
}) => (
  <div className="input-row" onKeyDown={onKeyDown}>
    <TextInput
      ref={inputRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder || 'Describe your project here...'}
      disabled={inputDisabled}
      maxLength={maxLength}
    />
    {showSendButton && (
      <SendButton
        className="send-btn--input-row"
        isLoading={isLoading}
        disabled={disabled}
        onClick={onClick}
        isStop={isStop}
        isStopping={isStopping}
      />
    )}
  </div>
);

// ... (Define AgentLogsIcon if needed, or just text)


interface ActionsRowProps {
  onAgentLogsClick?: () => void;
  onAttachClick?: () => void;
  attachDisabled?: boolean;
  onSendClick: () => void;
  isLoading: boolean;
  sendDisabled: boolean;
  onSettingsClick?: () => void;
  projectId: string | null;
  planAuto: boolean;
  onPlanAutoChange: (value: boolean) => void;
  onAgentsClick?: () => void;
  isByokPlan: boolean;
  isStop?: boolean;
  isStopping?: boolean;
  controlsDisabled?: boolean;
  sessionId?: string | null;
  isReadOnly?: boolean;
}

const useVisibility = (projectId: string) => {
  const projectSnapshot = useAppSelector((state) => (
    state.planning.activeProjectId === projectId
      ? state.planning.projectIsPublic
      : null
  ));
  const [localOverride, setLocalOverride] = useState<{
    projectId: string;
    value: boolean;
  } | null>(null);
  const override = localOverride?.projectId === projectId ? localOverride.value : null;
  const setIsPublic = useCallback((value: boolean) => {
    setLocalOverride({ projectId, value });
  }, [projectId]);

  // fetchProject already returns is_public alongside the messages. Reusing
  // that canonical bootstrap snapshot avoids a second project-detail GET from
  // the composer merely to render this dropdown.
  return {
    isPublic: override ?? projectSnapshot ?? true,
    loading: Boolean(projectId && override === null && projectSnapshot === null),
    setIsPublic,
  };
};

const ChatBoxVisibilityDropdown: React.FC<{
  projectId: string | null; sessionId?: string | null; disabled?: boolean; iconOnly?: boolean;
}> = ({ projectId, disabled = false, iconOnly = false }) => {
  const { isPublic, loading, setIsPublic } = useVisibility(projectId ?? '');
  const [landingVisibility, setLandingVisibility] = useState(true);

  if (projectId && loading) return null;

  const handleVisibilityChange = (pid: string | null, newIsPublic: boolean) => {
    if (!pid) {
      setLandingVisibility(newIsPublic);
      window.dispatchEvent(
        new globalThis.CustomEvent('landing_visibility_changed', { detail: { isPublic: newIsPublic } }),
      );
    } else {
      // The toggle API was already called by ProjectVisibilityDropdown, we just sync local state
      setIsPublic(newIsPublic);
    }
  };

  return (
    <ProjectVisibilityDropdown
      projectId={projectId}
      isPublic={projectId ? isPublic : landingVisibility}
      disabled={disabled}
      iconOnly={iconOnly}
      onVisibilityChange={handleVisibilityChange}
    />
  );
};

const KeysButton: React.FC<{ onClick?: () => void; disabled?: boolean }> = ({ onClick, disabled = false }) => {
  if (!onClick) return null;

  return (
    <button
      className="action-btn toolbar-pill actions-key-btn"
      onClick={onClick}
      title="BYOK Settings"
      type="button"
      disabled={disabled}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
      </svg>
      <span className="action-btn-label">Keys</span>
    </button>
  );
};

const PlanAutoToggle: React.FC<{
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled = false }) => (
  <label className="plan-auto-toggle actions-auto-toggle" title="Auto-trigger design agents after each message">
    <span className="plan-auto-label">Auto</span>
    <input
      type="checkbox" checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="plan-auto-checkbox"
    />
    <span className="plan-auto-slider" />
  </label>
);

const MobileSettingsButton: React.FC<{
  open: boolean;
  onClick: () => void;
  disabled?: boolean;
}> = ({ open, onClick, disabled = false }) => (
  <button
    className="action-btn actions-mobile-settings-btn"
    onClick={onClick}
    type="button"
    title="Input settings"
    aria-label="Input settings"
    aria-expanded={open}
    disabled={disabled}
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8.92 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09A1.65 1.65 0 0 0 19.4 15z" />
    </svg>
    <span className="action-btn-label">Settings</span>
  </button>
);

const ActionsStrip: React.FC<{
  onAttachClick?: () => void; onSettingsClick?: () => void;
  attachDisabled?: boolean;
  onSendClick: () => void; isLoading: boolean; sendDisabled: boolean;
  projectId: string | null; sessionId?: string | null; planAuto: boolean; onPlanAutoChange: (v: boolean) => void;
  onAgentsClick?: () => void;
  isByokPlan: boolean;
  isStop?: boolean;
  isStopping?: boolean;
  controlsDisabled?: boolean;
  isReadOnly?: boolean;
}> = ({ onAttachClick, attachDisabled = false, onSettingsClick, onSendClick, isLoading, sendDisabled, projectId, planAuto, onPlanAutoChange,
  onAgentsClick, isByokPlan, isStop, isStopping, controlsDisabled = false, sessionId, isReadOnly = false }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const compactColumn = useCompactComposer(projectId);
  const showSettingsButton = Boolean(projectId || onSettingsClick);
  const agentsButton = onAgentsClick && !isReadOnly ? (
    <button
      className="action-btn toolbar-pill actions-agents-btn"
      onClick={onAgentsClick}
      title="Agents"
      aria-label="Agents"
      type="button"
      disabled={controlsDisabled}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="8.5" cy="7" r="4" />
        <path d="M20 8v6" />
        <path d="M23 11h-6" />
      </svg>
      <span className="action-btn-label">Agents</span>
    </button>
  ) : null;

  return (
    <>
      <div className={`actions-row${settingsOpen ? ' actions-row--settings-open' : ''}`}>
        <div className="actions-left">
          <ProjectCreditSourceControl
            projectId={projectId}
            disabled={controlsDisabled}
            isReadOnly={isReadOnly}
          />
          {onAttachClick && (
            <AttachButton
              onClick={onAttachClick}
              disabled={attachDisabled || isReadOnly || controlsDisabled}
            />
          )}
          {!isReadOnly && !compactColumn && (
            <ChatBoxVisibilityDropdown projectId={projectId} sessionId={sessionId} disabled={controlsDisabled} />
          )}
          {!isReadOnly && <ModelSelectDropdown disabled={controlsDisabled} />}
          {!isReadOnly && compactColumn && (
            <ChatBoxVisibilityDropdown projectId={projectId} sessionId={sessionId} disabled={controlsDisabled} iconOnly />
          )}
        </div>
        <div className="actions-right">
          {!compactColumn && agentsButton}
          {!isByokPlan && <KeysButton onClick={onSettingsClick} disabled={controlsDisabled} />}
          {projectId && !isReadOnly && (
            <PlanAutoToggle checked={planAuto} onChange={onPlanAutoChange} disabled={controlsDisabled} />
          )}
          {showSettingsButton && (
            <MobileSettingsButton
              open={settingsOpen}
              disabled={controlsDisabled}
              onClick={() => {
                if (!projectId && onSettingsClick) {
                  onSettingsClick();

                  return;
                }
                setSettingsOpen((prev) => !prev);
              }}
            />
          )}
          {projectId && !isReadOnly && (
            <SendButton
              className="send-btn--actions-row"
              isLoading={isLoading}
              disabled={sendDisabled}
              onClick={onSendClick}
              isStop={isStop}
              isStopping={isStopping}
            />
          )}
        </div>
      </div>
      {settingsOpen && projectId && (
        <div className="actions-mobile-settings-panel actions-mobile-settings-panel--open">
          {compactColumn && agentsButton}
          {!isByokPlan && <KeysButton onClick={onSettingsClick} disabled={controlsDisabled} />}
          {!isReadOnly && (
            <PlanAutoToggle checked={planAuto} onChange={onPlanAutoChange} disabled={controlsDisabled} />
          )}
        </div>
      )}
    </>
  );
};

const ActionsRow: React.FC<ActionsRowProps> = ({
  onAttachClick, attachDisabled, onSendClick, isLoading, sendDisabled, onSettingsClick, onAgentsClick, projectId,
  sessionId, planAuto, onPlanAutoChange, isByokPlan, isStop, isStopping, controlsDisabled, isReadOnly,
}) => (
  <ActionsStrip
    onAttachClick={onAttachClick} attachDisabled={attachDisabled} onSettingsClick={onSettingsClick}
    onSendClick={onSendClick} isLoading={isLoading} sendDisabled={sendDisabled}
    projectId={projectId} sessionId={sessionId} planAuto={planAuto} onPlanAutoChange={onPlanAutoChange}
    onAgentsClick={onAgentsClick}
    isByokPlan={isByokPlan}
    isStop={isStop}
    isStopping={isStopping}
    controlsDisabled={controlsDisabled}
    isReadOnly={isReadOnly}
  />
);

// ============================================================================
// MAIN INPUT SECTION
// ============================================================================

interface ChatBoxInputProps {
  value?: string;
  onChange: (value: string) => void;
  onSend: (input: string, mediaUrls?: string[]) => void;
  isLoading?: boolean;
  onAgentLogsClick?: () => void;
  projectId: string | null;
  sessionId?: string | null;
  isAnyAgentRunning: boolean;
  hasTodoTasks: boolean;
  isByokPlan: boolean;
  isDeveloperMode?: boolean;
  isDeveloperModeProcessing?: boolean;
  isDeveloperModeStopping?: boolean;
  isPlanningRunning?: boolean;
  isPlanningCancelling?: boolean;
  sessionLocked?: boolean;
  sessionLockReason?: string | null;
  lockedByName?: string | null;
  lockedByImageUrl?: string | null;
  developerModeQuotaExhausted?: boolean;
  developerModeQuotaManagerName?: string | null;
  onContactManager?: () => void;
  onStopDeveloperMode?: () => void | Promise<void>;
  onStopPlanning?: () => void | Promise<void>;
  onSettingsClick?: () => void;
  userMessageCount: number;
  queuedDeveloperMessagesSlot?: React.ReactNode;
  isReadOnly?: boolean;
  allowMedia?: boolean;
  maxInputLength?: number;
  /** Overrides the default prompt text when nothing is blocking input. */
  placeholder?: string;
}

export const ChatBoxInput = forwardRef<HTMLTextAreaElement, ChatBoxInputProps>(
  // eslint-disable-next-line max-statements
  ({
    value: valueProp, onChange, onSend, isLoading = false, onAgentLogsClick,
    projectId, sessionId, isAnyAgentRunning: _isAnyAgentRunning, hasTodoTasks,
    isByokPlan, isDeveloperMode = false, isDeveloperModeProcessing = false, isDeveloperModeStopping = false,
    isPlanningRunning = false, isPlanningCancelling = false,
    sessionLocked = false, sessionLockReason, lockedByName, lockedByImageUrl,
    developerModeQuotaExhausted = false, developerModeQuotaManagerName,
    onContactManager, onStopDeveloperMode, onStopPlanning, onSettingsClick, userMessageCount,
    queuedDeveloperMessagesSlot, isReadOnly = false, placeholder: placeholderProp,
    allowMedia = true, maxInputLength,
  }, ref) => {
    const value = valueProp ?? '';
    const { isAuthenticated } = useAuth();
    const media = useMediaUpload(projectId, onSend, Boolean(projectId || isAuthenticated), sessionId);
    const docProcessing = useDocProcessing();
    const isDocProcessing = docProcessing !== null
      && docProcessing.status !== 'completed'
      && docProcessing.status !== 'complete'
      && docProcessing.status !== 'error';
    const handlers = createInputHandlers(
      value,
      isReadOnly || isLoading || media.isUploading || isDocProcessing,
      media.handleSendWithMedia,
    );
    const [planAuto, setPlanAuto] = useState(false);
    const [showAgentsModal, setShowAgentsModal] = useState(false);

    return (
      <>
        <ChatBoxInputView
          hasTodoTasks={hasTodoTasks}
          projectId={projectId}
          sessionId={sessionId}
          media={media} handlers={handlers} busy={isLoading || media.isUploading || isDocProcessing}
          isDocProcessing={isDocProcessing}
          placeholder={placeholderProp}
          value={value} onChange={onChange} inputRef={ref}
          onAgentLogsClick={onAgentLogsClick}
          planAuto={planAuto} onPlanAutoChange={setPlanAuto}
          isByokPlan={isByokPlan} onSettingsClick={onSettingsClick}
          isDeveloperMode={isDeveloperMode}
          isDeveloperModeProcessing={isDeveloperModeProcessing}
          isDeveloperModeStopping={isDeveloperModeStopping}
          isPlanningRunning={isPlanningRunning}
          isPlanningCancelling={isPlanningCancelling}
          sessionLocked={sessionLocked}
          sessionLockReason={sessionLockReason}
          lockedByName={lockedByName}
          lockedByImageUrl={lockedByImageUrl}
          developerModeQuotaExhausted={developerModeQuotaExhausted}
          developerModeQuotaManagerName={developerModeQuotaManagerName}
          onContactManager={onContactManager}
          onStopDeveloperMode={onStopDeveloperMode}
          onStopPlanning={onStopPlanning}
          onAgentsClick={projectId && !isReadOnly ? () => setShowAgentsModal(true) : undefined}
          userMessageCount={userMessageCount}
          queuedDeveloperMessagesSlot={queuedDeveloperMessagesSlot}
          isReadOnly={isReadOnly}
          allowMedia={allowMedia}
          maxInputLength={maxInputLength}
        />
        {projectId && (
          <AgentsModal
            isOpen={showAgentsModal}
            onClose={() => setShowAgentsModal(false)}
            projectId={projectId}
          />
        )}
      </>
    );
  }
);

ChatBoxInput.displayName = 'ChatBoxInput';

interface ChatBoxInputViewProps {
  hasTodoTasks: boolean;
  placeholder?: string;
  projectId: string | null;
  sessionId?: string | null;
  media: ReturnType<typeof useMediaUpload>;
  handlers: InputHandlers;
  busy: boolean;
  isDocProcessing: boolean;
  value: string;
  onChange: (value: string) => void;
  inputRef: React.Ref<HTMLTextAreaElement>;
  onAgentLogsClick?: () => void;
  planAuto: boolean;
  onPlanAutoChange: (value: boolean) => void;
  isByokPlan: boolean;
  isDeveloperMode?: boolean;
  isDeveloperModeProcessing?: boolean;
  isDeveloperModeStopping?: boolean;
  isPlanningRunning?: boolean;
  isPlanningCancelling?: boolean;
  sessionLocked?: boolean;
  sessionLockReason?: string | null;
  lockedByName?: string | null;
  lockedByImageUrl?: string | null;
  developerModeQuotaExhausted?: boolean;
  developerModeQuotaManagerName?: string | null;
  onContactManager?: () => void;
  onStopDeveloperMode?: () => void | Promise<void>;
  onStopPlanning?: () => void | Promise<void>;
  onSettingsClick?: () => void;
  onAgentsClick?: () => void;
  userMessageCount: number;
  queuedDeveloperMessagesSlot?: React.ReactNode;
  isReadOnly?: boolean;
  allowMedia: boolean;
  maxInputLength?: number;
}

const getInputPlaceholder = (
  isReadOnly: boolean | undefined,
  isDocProcessing: boolean,
  userMessageCount: number,
): string | undefined => {
  if (isReadOnly) return 'Reader access: this project is view-only';
  if (isDocProcessing) return 'Analyzing document, please wait…';
  if (userMessageCount > 0) return 'Discuss project details...';

  return undefined;
};

const ChatBoxInputView: React.FC<ChatBoxInputViewProps> = (props) => {
  const compactComposer = useCompactComposer(props.projectId);
  const { projectId,
    sessionId,
    media, handlers, busy, value, onChange, inputRef,
    onAgentLogsClick, planAuto, onPlanAutoChange,
    isByokPlan, isDeveloperMode, isDeveloperModeProcessing, isDeveloperModeStopping, onStopDeveloperMode,
    isPlanningRunning, isPlanningCancelling, onStopPlanning,
    sessionLocked, sessionLockReason, lockedByName, lockedByImageUrl,
    developerModeQuotaExhausted, developerModeQuotaManagerName, onContactManager,
    onSettingsClick, onAgentsClick, queuedDeveloperMessagesSlot, isReadOnly = false,
    allowMedia, maxInputLength } = props;
  const isLandingInput = projectId === null;
  // Planning cancellation uses the Send button as Stop. Task builds are a
  // separate worker flow and must use only the progress-bar build-stop button.
  const planningBlocked = Boolean(!isDeveloperMode && isPlanningRunning);
  const stopActive = Boolean((isDeveloperMode && isDeveloperModeProcessing) || planningBlocked);
  const stoppingActive = Boolean(isDeveloperMode ? isDeveloperModeStopping : isPlanningCancelling);
  const quotaBlocked = Boolean(isDeveloperMode && developerModeQuotaExhausted);
  const inputBlocked = isReadOnly || Boolean(sessionLocked) || quotaBlocked;
  const mediaDrop = useMediaDrop(media.addFiles, !allowMedia || isReadOnly || planningBlocked);
  const sendDisabled = isReadOnly || (stopActive
    ? stoppingActive
    : (Boolean(sessionLocked) || quotaBlocked || !handlers.trimmedInput || busy));
  const handleKeyDown = planningBlocked
    ? (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) event.preventDefault();
    }
    : handlers.handleKeyDown;
  const handlePrimaryAction = stopActive
    ? () => {
      if (isDeveloperMode) {
        void onStopDeveloperMode?.();
      } else {
        void onStopPlanning?.();
      }
    }
    : handlers.handleSendClick;
  const placeholder = planningBlocked
    ? 'Agents are running...'
    : sessionLocked
      ? (sessionLockReason || `Locked by ${lockedByName || 'another user'}`)
      : (quotaBlocked
        ? 'AI credit quota is exhausted'
        : (props.placeholder ?? getInputPlaceholder(isReadOnly, props.isDocProcessing, props.userMessageCount)));
  const lockNotice = sessionLocked ? (
    <div className="input-section__lock-note">
      {lockedByImageUrl ? <img src={lockedByImageUrl} alt="" /> : <i className="fa-solid fa-lock" />}
      <span>{sessionLockReason || `This session is locked by ${lockedByName || 'another user'}.`}</span>
    </div>
  ) : null;
  const quotaNotice = quotaBlocked ? (
    <button
      type="button"
      className="input-section__quota-button"
      onClick={onContactManager}
    >
      You are out of quota. Contact {developerModeQuotaManagerName || 'manager'}
    </button>
  ) : null;

  return (
    <div
      className={`input-section${projectId ? ' input-section--project' : ''}${compactComposer ? ' input-section--compact' : ''}${isReadOnly ? ' input-section--readonly' : ''}${mediaDrop.isDraggingFiles ? ' input-section--drag-active' : ''}`}
      {...mediaDrop.dropZoneProps}
    >
      {mediaDrop.isDraggingFiles && (
        <div className="input-section__drop-overlay" role="status" aria-live="polite">
          <AttachIcon />
          <span>Drop images or documents to attach</span>
        </div>
      )}
      {isDeveloperMode && <div className="input-section__mode-note">Developer mode</div>}
      {lockNotice}
      {quotaNotice}
      {queuedDeveloperMessagesSlot}
      {media.rejectedFiles.length > 0 && (
        <div className="media-size-error" role="alert">
          <span>
            File{media.rejectedFiles.length > 1 ? 's' : ''} too large (max 10 MB):{' '}
            <strong>{media.rejectedFiles.join(', ')}</strong>
          </span>
          <button
            className="media-size-error__dismiss"
            onClick={media.clearRejected}
            aria-label="Dismiss"
            type="button"
          >
            ✕
          </button>
        </div>
      )}
      {media.uploadError && (
        <div className="media-size-error" role="alert">
          <span>{media.uploadError}</span>
          <button
            className="media-size-error__dismiss"
            onClick={media.clearUploadError}
            aria-label="Dismiss"
            type="button"
          >
            ✕
          </button>
        </div>
      )}
      <MediaPreview
        files={media.selectedFiles}
        onRemove={media.handleRemoveFile}
        isUploading={media.isUploading}
        uploadingFiles={media.uploadingFiles}
      />
      <div className="input-section__message">
        <InputRow
          value={value} onChange={onChange} onKeyDown={handleKeyDown}
          onClick={handlePrimaryAction} isLoading={busy} disabled={sendDisabled}
          inputRef={inputRef} showSendButton={isLandingInput || !isReadOnly}
          placeholder={placeholder}
          isStop={stopActive}
          isStopping={stoppingActive}
          inputDisabled={inputBlocked}
          maxLength={maxInputLength}
        />
      </div>
      {isReadOnly && (
        <div className="input-section__readonly-note">
          Reader access lets you view project work. Ask an admin for Writer access to chat or trigger agents.
        </div>
      )}
      <div className="input-section__buttons">
        <ActionsRow
          onAgentLogsClick={onAgentLogsClick}
          onAttachClick={media.handleAttachClick}
          attachDisabled={!allowMedia}
          onSendClick={handlePrimaryAction}
          isLoading={busy}
          sendDisabled={sendDisabled}
          onSettingsClick={onSettingsClick}
          onAgentsClick={onAgentsClick}
          projectId={projectId}
          sessionId={sessionId}
          planAuto={planAuto}
          onPlanAutoChange={onPlanAutoChange}
          isByokPlan={isByokPlan}
          isStop={stopActive}
          isStopping={stoppingActive}
          controlsDisabled={planningBlocked}
          isReadOnly={isReadOnly}
        />
      </div>
      {allowMedia && <input {...media.fileInputProps} />}
    </div>
  );
};
